/**
 * Timecode-based metadata. A stratum is a configurable layer of annotations
 * (content description, shot type, people, …); a segment is one annotation
 * with in and out points on an asset version, in ticks from the first frame.
 */
import { z } from 'zod';

export const STRATUM_KINDS = ['text', 'vocabulary', 'tags'] as const;
export type StratumKind = (typeof STRATUM_KINDS)[number];

export const StratumInput = z.object({
  label: z.string().trim().min(1).max(80),
  description: z.string().max(300).default(''),
  kind: z.enum(STRATUM_KINDS).default('text'),
  options: z.array(z.object({ value: z.string().min(1).max(80), label: z.string().min(1).max(80) })).max(200).default([]),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).default('#8b7cf6'),
  searchable: z.boolean().default(true),
});
export type StratumInput = z.infer<typeof StratumInput>;

/** Changes to a layer: only what is sent is changed (no defaults). */
export const StratumPatch = z.object({
  label: z.string().trim().min(1).max(80).optional(),
  description: z.string().max(300).optional(),
  kind: z.enum(STRATUM_KINDS).optional(),
  options: z.array(z.object({ value: z.string().min(1).max(80), label: z.string().min(1).max(80) })).max(200).optional(),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
  searchable: z.boolean().optional(),
  position: z.number().int().min(0).max(1000).optional(),
  archived: z.boolean().optional(),
});

export interface StratumDTO extends StratumInput {
  id: string;
  position: number;
  archived: boolean;
  /** The layer's segment count: present only when GET /api/v1/strata is asked with ?counts=true (1.8.1). */
  segments?: number;
  /** A layer PRISM writes (the QC layer, s_qc): read-only for people. */
  system?: boolean;
}

export const SegmentInput = z.object({
  stratumId: z.string().regex(/^s_[a-z0-9_]{1,40}$/),
  startTicks: z.number().int().nonnegative(),
  endTicks: z.number().int().positive(),
  text: z.string().max(5000).default(''),
  values: z.array(z.string().min(1).max(80)).max(50).default([]),
});
export type SegmentInput = z.infer<typeof SegmentInput>;

export interface SegmentDTO extends SegmentInput {
  id: string;
  /** 1.5.0: 'shots' when Fill from shots made it (and on the Shots layer); null for one a person added. */
  source?: import('./shots.ts').SegmentSource | null;
  versionId: string;
  updatedAt: string;
  updatedBy: string | null;
  /**
   * 1.6.0 (ai-describe.ts): an AI's suggestion. suggestedBy is `mcp:<token name>` or `flow:<run id>`; acceptedAt is set
   * once a person accepts it (or edits it, which accepts). A pending suggestion has suggestedBy and no acceptedAt.
   * Absent before 1.6.0; null for a person's segment.
   */
  suggestedBy?: string | null;
  suggestedAt?: string | null;
  acceptedBy?: string | null;
  acceptedAt?: string | null;
}

/** A segment found by search, with enough context to show and open it. */
export interface MomentDTO {
  /** Which layer the moment comes from: a timecoded layer segment (default) or a subtitle cue. */
  source?: 'stratum' | 'subtitle';
  /** The segment id, or the cue id for a subtitle moment. */
  segmentId: string;
  assetId: string;
  assetTitle: string;
  /** The layer id, or `_subtitles` for a subtitle moment. */
  stratumId: string;
  /** The layer name, or the subtitle track's label. */
  stratumLabel: string;
  color: string;
  startTicks: number;
  endTicks: number;
  text: string;
  values: string[];
  /** Source timecode of the in point, when the frame rate is known. */
  timecode: string | null;
  thumbnailUrl: string | null;
}

/** Stable stratum id from a label: "Content description" → s_content_description. */
export function stratumIdFor(label: string): string {
  const slug = label.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 36);
  return `s_${slug || 'layer'}`;
}
